import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, CornerDownRight } from "lucide-react";
import { askAnalyst } from "../../lib/api";

export const AskLongview = ({ ticker, questions = [] }) => {
  const [thread, setThread] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  const ask = async (question) => {
    if (loadingId) return;
    setLoadingId(question.id);
    setThread((t) => [...t, { role: "user", text: question.label, id: `${question.id}-q-${t.length}` }]);
    try {
      const res = await askAnalyst(ticker, question.id);
      setThread((t) => [...t, { role: "analyst", text: res.answer, id: `${question.id}-a-${t.length}` }]);
    } catch (e) {
      setThread((t) => [...t, { role: "analyst", text: "Sorry — LONGVIEW couldn't answer that right now.", id: `err-${t.length}` }]);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div data-testid="company-ask-longview" className="lv-panel lv-top-edge overflow-hidden">
      <div className="flex items-center justify-between border-b border-white/[0.07] px-5 py-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md border border-amber-500/30 bg-amber-500/10">
            <Sparkles className="h-4 w-4 text-amber-400" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-white">Ask LONGVIEW</h3>
            <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Explore the business behind the numbers</p>
          </div>
        </div>
        <span className="rounded border border-indigo-500/25 bg-indigo-500/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-indigo-300">LONGVIEW AI</span>
      </div>

      <div className="max-h-[360px] space-y-3 overflow-y-auto px-5 py-4" aria-live="polite" aria-busy={Boolean(loadingId)}>
        {thread.length === 0 && (
          <p className="text-sm leading-relaxed text-zinc-400">
            Ask a question about this company in plain English. LONGVIEW answers using its structured research — tap a question below to begin.
          </p>
        )}
        {thread.map((m) => (
          m.role === "user" ? (
            <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="flex justify-end">
              <div className="max-w-[85%] rounded-lg rounded-br-sm border border-white/10 bg-[#14171C] px-3 py-2 text-sm text-zinc-200">
                {m.text}
              </div>
            </motion.div>
          ) : (
            <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="flex gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded border border-amber-500/25 bg-amber-500/10">
                <Sparkles className="h-3 w-3 text-amber-400" />
              </span>
              <div className="max-w-[85%] whitespace-pre-line rounded-lg rounded-tl-sm border border-amber-500/15 bg-amber-500/[0.04] px-3.5 py-2.5 text-sm leading-relaxed text-zinc-200">
                {m.text}
              </div>
            </motion.div>
          )
        ))}
        {loadingId && (
          <div role="status" className="flex items-center gap-2 pl-8 font-mono text-xs text-zinc-500">
            <span className="lv-dot-pulse">Analysing</span>
          </div>
        )}
      </div>

      <div className="border-t border-white/[0.07] p-3">
        <div className="mb-2 kicker px-1">Suggested questions</div>
        <div className="flex flex-wrap gap-2">
          {questions.map((qq) => (
            <button
              type="button"
              key={qq.id}
              data-testid={`ask-question-${qq.id}`}
              onClick={() => ask(qq)}
              disabled={!!loadingId}
              className="lv-press group inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-[#0B0C0E] px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200 disabled:opacity-40"
            >
              <CornerDownRight className="h-3 w-3 text-zinc-600 group-hover:text-amber-400" />
              {qq.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
